import nipplejs from 'nipplejs';
import { CAM_ORBIT_SPEED } from '../config';

// Camera-drag must yield to any tap that lands on a UI button. We collect
// these by id so adding a new button is one place to edit.
const UI_BUTTON_IDS = ['touch-jump', 'touch-action', 'mh-end-shift-mobile'];

export class TouchInput {
  private joystickX = 0;
  private joystickZ = 0;
  private jumping = false;
  private actionEdge = false; // true for one frame after the action button is tapped
  private cameraDeltaX = 0;
  private cameraDeltaY = 0;
  private manager: ReturnType<typeof nipplejs.create> | null = null;
  private lastTouchX = 0;
  private lastTouchY = 0;
  private cameraTouch: number | null = null;
  private actionBtn: HTMLButtonElement | null = null;
  private actionLabel = '';
  private actionActive = false;

  constructor() {
    const zone = document.getElementById('joystick-zone');
    if (zone) {
      zone.style.display = 'block';
      this.manager = nipplejs.create({
        zone,
        mode: 'semi',
        catchDistance: 80,
        color: 'rgba(255,255,255,0.3)',
        size: 120,
      });

      this.manager.on('move', (_evt, data) => {
        if (data.vector) {
          this.joystickX = data.vector.x;
          this.joystickZ = -data.vector.y;
        }
      });

      this.manager.on('end', () => {
        this.joystickX = 0;
        this.joystickZ = 0;
      });
    }

    const jumpBtn = document.getElementById('touch-jump') as HTMLButtonElement | null;
    if (jumpBtn) {
      jumpBtn.style.display = 'flex';
      jumpBtn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.jumping = true;
      }, { passive: false });
      jumpBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        this.jumping = false;
      }, { passive: false });
    }

    this.actionBtn = document.getElementById('touch-action') as HTMLButtonElement | null;
    if (this.actionBtn) {
      this.actionBtn.style.display = 'flex';
      // Edge-trigger on touchstart: feels snappier than waiting for click,
      // and avoids the 300ms delay still lurking on some Android browsers.
      this.actionBtn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.actionEdge = true;
      }, { passive: false });
      // Mouse fallback for desktop dev-tools mobile emulation.
      this.actionBtn.addEventListener('click', () => {
        this.actionEdge = true;
      });
    }

    window.addEventListener('touchstart', this.onTouchStart, { passive: false });
    window.addEventListener('touchmove', this.onTouchMove, { passive: false });
    window.addEventListener('touchend', this.onTouchEnd, { passive: false });
  }

  /** Returns true if the touch lands inside any UI button rect. */
  private touchIsOnUI(touch: Touch): boolean {
    for (const id of UI_BUTTON_IDS) {
      const el = document.getElementById(id);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (
        touch.clientX >= r.left && touch.clientX <= r.right &&
        touch.clientY >= r.top && touch.clientY <= r.bottom
      ) return true;
    }
    return false;
  }

  private onTouchStart = (e: TouchEvent) => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      if (touch.clientX > window.innerWidth * 0.5 && this.cameraTouch === null) {
        if (this.touchIsOnUI(touch)) continue;
        this.cameraTouch = touch.identifier;
        this.lastTouchX = touch.clientX;
        this.lastTouchY = touch.clientY;
      }
    }
  };

  private onTouchMove = (e: TouchEvent) => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      if (touch.identifier === this.cameraTouch) {
        this.cameraDeltaX += (touch.clientX - this.lastTouchX) * CAM_ORBIT_SPEED * 2;
        this.cameraDeltaY += (touch.clientY - this.lastTouchY) * CAM_ORBIT_SPEED * 2;
        this.lastTouchX = touch.clientX;
        this.lastTouchY = touch.clientY;
      }
    }
  };

  private onTouchEnd = (e: TouchEvent) => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this.cameraTouch) {
        this.cameraTouch = null;
      }
    }
  };

  getMoveX(): number { return this.joystickX; }
  getMoveZ(): number { return this.joystickZ; }
  isJump(): boolean {
    const j = this.jumping;
    this.jumping = false;
    return j;
  }
  consumeAction(): boolean {
    const a = this.actionEdge;
    this.actionEdge = false;
    return a;
  }

  /** Update the on-screen action button label and active highlight. No-op
   * if the new state matches the cached one — avoids per-frame DOM writes. */
  setActionState(label: string, active: boolean, disabled = false) {
    if (!this.actionBtn) return;
    if (label !== this.actionLabel) {
      this.actionBtn.textContent = label;
      this.actionLabel = label;
    }
    if (active !== this.actionActive) {
      this.actionBtn.classList.toggle('is-active', active);
      this.actionActive = active;
    }
    this.actionBtn.classList.toggle('is-disabled', disabled);
  }

  consumeCameraDelta(): { x: number; y: number } {
    const d = { x: this.cameraDeltaX, y: this.cameraDeltaY };
    this.cameraDeltaX = 0;
    this.cameraDeltaY = 0;
    return d;
  }

  dispose() {
    this.manager?.destroy();
    window.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('touchend', this.onTouchEnd);
  }
}
